import React, { useState, useEffect, useCallback, useMemo } from 'react';
import rfdc from 'rfdc';
import { Button, ScrollableViewport } from '@ihsm/ui-shared';
import { useGetMetricsQuery } from 'services/newApi/metricsApi';
import { useGetRefPricingQuery } from 'services/newApi/configsApi';
import { useAppSelector } from 'state/store';
import {
  metricsForReportColumns,
  refPricingSelector,
} from 'state/store/selectors';
import { ValidationFormField } from 'shared/types';
import { FormSearchInput } from 'shared/components/FormSearchInput';
import {
  TransparentButton,
  SmallTransparentButton,
} from 'shared/components/Buttons';
import { FormFailedIconMessage } from 'shared/components/FormFailedIconMessage';
import { ListWithItemDrag } from 'shared/components/ListWithRowDrag';
import { ColumnDraggableItem } from './ColumnDraggableItem';
import { TABS_COLUMN_OPTIONS } from '../constants';
import { FormColumnItem } from '../types';
import { ColumnsTabsSelection } from './ColumnsTabsSelection';
import { NoReportData } from './NoReportData';
import './ColumnsSelectionArea.scss';

interface ColumnsSelectionAreaProps {
  data: FormColumnItem[];
  onDataChange: (data: FormColumnItem[]) => void;
  isDisabled?: boolean;
  validation?: ValidationFormField;
}

export const ColumnsSelectionArea: React.FC<ColumnsSelectionAreaProps> = ({
  data = [],
  onDataChange = () => {},
  isDisabled = false,
  validation,
}) => {
  const onDataChangeCloned = useCallback(
    (updatedData: FormColumnItem[]) => {
      // TODO: Remove this clone before updating the state
      // with the implementation of the redux store we need to remove this.
      // Because the state is using the same object, we need a cloned version to keep it separated from the imported one in the line 10
      onDataChange(rfdc()(updatedData));
    },
    [onDataChange]
  );

  const appState = useAppSelector(
    (state) => state,
    (prev, next) =>
      prev.metricsState.metricsList === next.metricsState.metricsList
  );
  const metricsList = metricsForReportColumns(appState);
  const refPricing = refPricingSelector(appState);

  const columnListItems = useMemo(
    () => [...refPricing, ...metricsList],
    [metricsList, refPricing]
  );

  const [selectedTab, setSelectedTab] = useState(TABS_COLUMN_OPTIONS[0]);
  const [listSource, setListSource] =
    useState<FormColumnItem[]>(columnListItems);
  const [selectedList, setSelectedList] = useState<FormColumnItem[]>([]);

  const [isOnMultipleSelection, setIsMultipleSelection] = useState(false);
  const [search, setSearch] = useState<string>();

  const handleSearch = (value?: string) => setSearch(value);

  const searchOptions = useMemo(
    () => columnListItems.map(({ category, name }) => `${category} / ${name}`),
    [columnListItems]
  );

  const handleDataChange = useCallback(
    (updatedData: FormColumnItem[]) => {
      onDataChangeCloned(updatedData);
    },
    [onDataChangeCloned]
  );

  const handleDeletion = useCallback(
    (index: number) => {
      const updated = [...data].filter((_, i) => i !== index);
      onDataChangeCloned(updated);
    },
    [data, onDataChangeCloned]
  );

  const handleUpdateOption = useCallback(
    (value: string, index: number) => {
      const updated = [...data];
      updated[index].unit = value;
      onDataChangeCloned(updated);
    },
    [data, onDataChangeCloned]
  );

  const handleAddNew = useCallback(() => {
    const divider = ' / ';

    if (search && search !== '' && search.includes(divider)) {
      const [category, name] = search.split(divider);
      const found = columnListItems.find(
        (o) => o.name === name && o.category === category
      );

      if (found) {
        const updated = [...data, found];
        onDataChangeCloned(updated);
      }

      setSearch('');
    } else {
      setIsMultipleSelection(true);
    }
  }, [columnListItems, data, onDataChangeCloned, search]);

  const handleAddMultipleFromTabs = useCallback(() => {
    onDataChangeCloned([...data, ...selectedList]);
    setSelectedList([]);
    setIsMultipleSelection(false);
  }, [data, onDataChangeCloned, selectedList]);

  const handleSelectionChange = useCallback(
    (updatedData: FormColumnItem[]) => {
      if (updatedData && Array.isArray(updatedData) && updatedData.length > 0) {
        const cleanedList = selectedList.filter(
          (columnItem) => columnItem.category !== updatedData[0].category
        );
        setSelectedList([...cleanedList, ...updatedData]);
      }
    },
    [selectedList]
  );

  useEffect(() => {
    const filtered = columnListItems.filter(
      (columnItem) => columnItem?.category === selectedTab
    );

    const sorted = filtered.sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });

    setListSource(sorted);
  }, [columnListItems, selectedTab]);

  useGetMetricsQuery();
  useGetRefPricingQuery();

  return (
    <>
      <div className="columns-selection-area">
        <h4 className="columns-selection-area__title">Columns</h4>

        <div className="columns-selection-area__search-area">
          <FormSearchInput
            searchInput={search}
            setSearchInput={setSearch}
            placeholder="Search"
            optionsList={searchOptions}
            onSearch={handleSearch}
            disabled={isDisabled}
            actionButton={
              isOnMultipleSelection ? (
                <>
                  <TransparentButton
                    text="&times; Cancel"
                    onClick={() => {
                      setSelectedList([]);
                      setSelectedTab(TABS_COLUMN_OPTIONS[0]);
                      setIsMultipleSelection(false);
                    }}
                  />
                  <Button
                    onClick={handleAddMultipleFromTabs}
                    text="+ Add Selected"
                  />
                </>
              ) : (
                <Button
                  onClick={handleAddNew}
                  text="+ Add Column"
                  disabled={isDisabled}
                />
              )
            }
          />
        </div>

        {!isOnMultipleSelection ? (
          <>
            <ScrollableViewport vertical>
              <div
                className={`columns-selection-area__draggable-box ${
                  validation?.isInvalid
                    ? 'columns-selection-area__draggable-box--invalid'
                    : ''
                }`}
              >
                {!data.length && <NoReportData sectionType="Column" />}
                <ListWithItemDrag
                  keyExpr="name"
                  data={data}
                  onDataChange={handleDataChange}
                  disabled={isDisabled}
                  itemTemplate={(
                    { category, name, unit }: FormColumnItem,
                    index
                  ) => (
                    <ColumnDraggableItem
                      name={name}
                      category={category}
                      unit={unit}
                      onRemove={() => handleDeletion(index)}
                      onChangeOption={(value) =>
                        handleUpdateOption(value, index)
                      }
                    />
                  )}
                />
              </div>
            </ScrollableViewport>
            <FormFailedIconMessage
              message={validation?.message}
              isActive={validation?.isInvalid}
            />
          </>
        ) : (
          <ColumnsTabsSelection
            tabs={TABS_COLUMN_OPTIONS}
            selectedTab={selectedTab}
            onChangeTab={(tab) => setSelectedTab(tab)}
            listData={listSource}
            selectedItems={selectedList}
            onDataChange={handleSelectionChange}
          />
        )}
      </div>
      {data && data.length && !isOnMultipleSelection ? (
        <div className="columns-selection-area__clear-all-button">
          <SmallTransparentButton
            onClick={() => onDataChange([])}
            text="Clear All"
            disabled={isDisabled}
          />
        </div>
      ) : null}
    </>
  );
};
